/**
 * QR Service — aligned with AUTH_V2_REACT_FRONTEND_DOCS.md
 *
 * The web frontend acts as the **approver** — it scans/confirms QR codes
 * displayed on TV / other devices. It does NOT generate QR codes itself.
 *
 */
import axiosInstance from "@/config/axiosInstance";
import { getAuthApiRoute } from "@/helpers/api.util";

/**
 * Unwrap the API's standard success envelope.
 */
function unwrap(res) {
    const d = res?.data ?? res;

    // The backend error shape: { message, statusCode, details }
    if (d?.statusCode && d.statusCode >= 400) {
        const err = new Error(d.message || "خطای سرور");
        err.statusCode = d.statusCode;
        err.details = d.details;
        throw err;
    }

    // The successful shape may be { data: { ... } } or just { ... }
    return d?.data ?? d;
}

/**
 * Scan (acknowledge) a QR code.
 * Can use either (qrId + sessionId) or temporaryCode.
 *
 * POST /qr/scan
 *
 * @param {{ qrId?: string, sessionId?: string, temporaryCode?: string, mobileNumber: number, accessToken: string }} params
 * @returns {{ success: boolean, message: string, requiresAuth: boolean, userInfo: object, device: string, ip: string, qrId: string, sessionId: string }}
 */
export const scanQRCode = async (params) => {
    const res = await axiosInstance.post(
        getAuthApiRoute("qr/scan"),
        params,
        { skipAuth: true, skipAuthRefresh: true }
    );
    return unwrap(res);
};


/**
 * Confirm a previously scanned QR code, completing the login on the requesting device.
 *
 * POST /qr/confirm
 *
 * @param {{ qrId: string, sessionId: string, mobileNumber: number, accessToken: string }} params
 */
export const confirmQRCode = async (params) => {
    const res = await axiosInstance.post(
        getAuthApiRoute("qr/confirm"),
        params,
        { skipAuth: true, skipAuthRefresh: true }
    );
    return unwrap(res);
};

/**
 * Cancel a QR login attempt.
 *
 * POST /qr/cancel
 *
 * @param {string} qrId
 * @param {string} sessionId
 */
export const cancelQRCode = async (qrId, sessionId) => {
    const res = await axiosInstance.post(
        getAuthApiRoute("qr/cancel"),
        { qrId, sessionId },
        { skipAuth: true, skipAuthRefresh: true }
    );
    return unwrap(res);
};

/**
 * Poll QR status (used by the device that generated the QR).
 *
 * POST /qr/status
 *
 * @param {string} qrId
 * @param {string} sessionId
 * @returns {{ status: 'pending'|'scanned'|'authenticated'|'expired'|'cancelled', message: string, tokens?: { accessToken, refreshToken } }}
 */
export const getQRStatus = async (qrId, sessionId) => {
    const res = await axiosInstance.post(
        getAuthApiRoute("qr/status"),
        { qrId, sessionId },
        { skipAuth: true, skipAuthRefresh: true }
    );
    return unwrap(res);
};
